import type { ElementType, PointerEvent as ReactPointerEvent, ReactNode, Ref } from 'react'
import { useEffect, useRef } from 'react'
import { haptic, type HapticRole } from './haptics'
import { cx } from './cx'

/**
 * Нажимаемая поверхность — самый важный компонент с точки зрения ощущения:
 * через него проходит КАЖДОЕ касание. Не делай нажимаемые элементы на голом
 * <button onClick>: получится кнопка, которая никак не отвечает пальцу, а это
 * ровно то, чем интерфейс выдаёт своё «страничное» происхождение.
 *
 * Разделение обязанностей намеренное:
 *   • РАСПОЗНАВАНИЕ нажатия отдано браузеру — действие вызывается из onClick,
 *     который уже знает про отмену жеста прокруткой, отпускание за пределами
 *     элемента и клавиатуру (Enter/Space на настоящей кнопке).
 *   • РИСОВАНИЕ отклика — CSS. Компонент переставляет data-phase прямо на
 *     DOM-узле, БЕЗ обновления состояния React: касание не вызывает ни одной
 *     перерисовки, переход ведёт компоузер. Поэтому нажатие отыгрывает
 *     плавно, даже когда главный поток занят загрузкой данных.
 *
 * Состояний три: idle → pressed → held. Удержание — отдельное событие.
 */

/** Насколько палец может «уехать», не отменяя нажатие (системный touch slop). */
const SLOP_PX = 12

/** Насколько проседает элемент — задаёт роль цели, а не вкус. */
export type PressSquish = 'none' | 'row' | 'card' | 'chip' | 'key' | 'icon'

export interface PressOwnProps {
  as?: ElementType
  onPress?: (event: React.MouseEvent) => void
  onLongPress?: (event: ReactPointerEvent) => void
  delayLongPress?: number
  disabled?: boolean
  squish?: PressSquish
  /** По умолчанию отдача ВЫКЛЮЧЕНА — см. политику в haptics.ts. */
  haptic?: HapticRole
  /**
   * Отдача на отпускании: касание ещё не действие, его можно увести с кнопки.
   * Исключение — клавиши кода, где по цифрам бьют подряд и отдача обязана
   * совпасть с касанием, иначе читается как лаг.
   */
  hapticOn?: 'down' | 'up'
  /**
   * Срывать нажатие, если палец уехал дальше slop. Нужно там, где касание может
   * оказаться свайпом (карусель, ряд в ленте). Переключателю в форме свайпать
   * нечего: при быстрых тапах палец уже едет к соседнему варианту, и отмена
   * глотала бы касания — выбор «застывал».
   */
  cancelOnMove?: boolean
  /**
   * Когда срабатывает действие касанием. По умолчанию — на click. iOS (Safari
   * и WKWebView оболочки) склеивает быстрые тапы в двойной: второй click не
   * приходит вовсе, и переключатель, который тапают подряд, «застывает».
   * 'up' — действие на отпускании пальца над элементом, следующий за ним click
   * гасится. Только для идемпотентных действий: выбор варианта, а не «Купить».
   * Мышь и клавиатура по-прежнему идут через click.
   */
  trigger?: 'click' | 'up'
  className?: string
  children?: ReactNode
  elementRef?: Ref<HTMLElement>
}

/**
 * Свои свойства плюс что угодно ещё (role, aria-*, data-*) — они уезжают на
 * узел как есть. Индекс объявлен пересечением, а не внутри интерфейса: иначе
 * Omit<PressProps, …> стёр бы все именованные свойства.
 */
export type PressProps = PressOwnProps & Record<string, unknown>

interface Gesture {
  down: boolean
  longFired: boolean
  /** Палец уехал дальше slop — жест перестал быть нажатием. */
  moved: boolean
  /** Действие уже отработало на отпускании — click за ним лишний. */
  firedOnUp: boolean
  timer: ReturnType<typeof setTimeout> | undefined
  x: number
  y: number
}

export function Press({
  as: Tag = 'button',
  onPress,
  onLongPress,
  delayLongPress = 420,
  disabled = false,
  squish = 'card',
  haptic: hapticRole = 'none',
  hapticOn = 'up',
  cancelOnMove = true,
  trigger = 'click',
  className,
  children,
  elementRef,
  type,
  onPointerDown,
  onPointerUp,
  onClick,
  ...rest
}: PressProps) {
  const nodeRef = useRef<HTMLElement | null>(null)

  /**
   * Всё состояние жеста — в ref. Перерисовка на касание не нужна, а порядок
   * «удержание сработало» и «палец отпущен» браузером не гарантирован: если
   * удержание доедет ПОСЛЕ отпускания, элемент останется зажатым.
   */
  const gesture = useRef<Gesture>({
    down: false,
    longFired: false,
    moved: false,
    firedOnUp: false,
    timer: undefined,
    x: 0,
    y: 0,
  })

  const setPhase = (phase: 'idle' | 'pressed' | 'held') => {
    if (nodeRef.current) nodeRef.current.dataset.phase = phase
  }

  const attach = (el: HTMLElement | null) => {
    nodeRef.current = el
    if (typeof elementRef === 'function') elementRef(el)
    else if (elementRef) (elementRef as { current: HTMLElement | null }).current = el
  }

  // Таймер удержания обязан умереть вместе с компонентом: элемент может
  // размонтироваться с пальцем на экране.
  useEffect(() => {
    const g = gesture.current
    return () => clearTimeout(g.timer)
  }, [])

  const release = () => {
    const g = gesture.current
    g.down = false
    clearTimeout(g.timer)
    setPhase('idle')
  }

  const handlePointerDown = (event: ReactPointerEvent) => {
    if (disabled) return
    // Правая кнопка и колесо — не нажатие.
    if (event.pointerType === 'mouse' && event.button !== 0) return

    const g = gesture.current
    g.down = true
    g.longFired = false
    g.moved = false
    g.firedOnUp = false
    g.x = event.clientX
    g.y = event.clientY
    setPhase('pressed')

    if (hapticOn === 'down') haptic(hapticRole)

    if (onLongPress) {
      g.timer = setTimeout(() => {
        // Палец уже ушёл — удержание опоздало.
        if (!g.down) return
        g.longFired = true
        setPhase('held')
        // Удержание ЗАМЕНЯЕТ нажатие, отдача здесь своя, более весомая.
        haptic('hold')
        onLongPress(event)
      }, delayLongPress)
    }

    ;(onPointerDown as ((e: ReactPointerEvent) => void) | undefined)?.(event)
  }

  const fire = (event: React.MouseEvent) => {
    if (hapticOn === 'up') haptic(hapticRole)
    onPress?.(event)
    ;(onClick as ((e: React.MouseEvent) => void) | undefined)?.(event)
  }

  /**
   * Касание отпустили над элементом. Захват касания неявный — pointerup придёт
   * сюда, даже если палец съехал за край, поэтому сверяемся с рамкой узла.
   */
  const isTapUp = (event: ReactPointerEvent) => {
    const g = gesture.current
    const node = nodeRef.current
    if (event.pointerType === 'mouse' || !g.down || g.longFired || g.moved || !node) return false
    const box = node.getBoundingClientRect()
    return (
      event.clientX >= box.left &&
      event.clientX <= box.right &&
      event.clientY >= box.top &&
      event.clientY <= box.bottom
    )
  }

  const handlePointerUp = (event: ReactPointerEvent) => {
    const tap = trigger === 'up' && !disabled && isTapUp(event)
    release()
    if (tap) {
      gesture.current.firedOnUp = true
      fire(event)
    }
    ;(onPointerUp as ((e: ReactPointerEvent) => void) | undefined)?.(event)
  }

  /**
   * Палец уехал дальше slop — это ЖЕСТ (прокрутка, свайп), а не нажатие.
   * Флаг `moved` обязателен: вертикальная прокрутка отменяется браузером сама,
   * но горизонтальный жест заканчивается pointerup на том же элементе, и
   * браузер честно присылает click.
   */
  const handlePointerMove = (event: ReactPointerEvent) => {
    const g = gesture.current
    if (!g.down || !cancelOnMove) return
    if (Math.abs(event.clientX - g.x) > SLOP_PX || Math.abs(event.clientY - g.y) > SLOP_PX) {
      g.moved = true
      release()
    }
  }

  const handleClick = (event: React.MouseEvent) => {
    if (disabled) return
    const g = gesture.current
    // Удержание ЗАМЕНЯЕТ нажатие, но браузер всё равно пришлёт click — гасим,
    // иначе сработают оба действия. То же и для сорванного жестом.
    if (g.longFired || g.moved) {
      g.longFired = false
      g.moved = false
      return
    }
    // Касание уже отработало на отпускании; на iOS этот click мог и не прийти.
    if (g.firedOnUp) {
      g.firedOnUp = false
      return
    }
    fire(event)
  }

  // Долгое нажатие в мобильном браузере норовит вызвать системное меню поверх
  // нашего собственного.
  const handleContextMenu = onLongPress ? (e: React.MouseEvent) => e.preventDefault() : undefined

  const isButton = Tag === 'button'

  return (
    <Tag
      ref={attach}
      // Кнопка без type по стандарту считается submit — внутри формы такая
      // кнопка отправляла бы форму вместо своего действия.
      type={isButton ? ((type as string | undefined) ?? 'button') : type}
      disabled={isButton && disabled ? true : undefined}
      aria-disabled={!isButton && disabled ? true : undefined}
      className={cx('dx-press', className)}
      data-phase="idle"
      data-squish={squish}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={release}
      onPointerLeave={release}
      onPointerMove={handlePointerMove}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      {...rest}
    >
      {children}
    </Tag>
  )
}
